import { object, string } from 'yup';
import { Form, Formik } from 'formik';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import mixpanel from 'mixpanel-browser';

// Hooks
import { useAppDispatch } from 'hooks/store';
import { useForceVin } from './context';

// Components
import { PrimaryButton, SecondaryButton } from 'components/Buttons';
import FormField from 'components/FormField';

// Styles
import { Modal } from 'components/Modals/styles';
import { ModalContent, ModalFooter, ModalHeader, ModalHeaderTitle } from 'styles/modals';

// Services
import { DevicesType } from 'services/resources';
import { forceVin } from 'services/FleetSettings/thunks';

// Constants
import { FLASH_MESSAGE_TYPES } from 'constants/common';
import { ANALYTICS_EVENTS } from 'constants/mixpanel.constants';

interface Props {
  device: DevicesType;
  closeModal: () => void;
  addFlashMessage: (text: string, type: FLASH_MESSAGE_TYPES) => void;
}

const VINField = 'VIN';

const Validation = (formatFn: IntlShape['formatMessage']) =>
  object({
    [VINField]: string()
      .label(formatFn({ id: 'views.FleetSettings.Telematics.Modals.ForceVIN.vin' }))
      .required()
      .length(17),
  });

const ForceVinForm = ({ device, closeModal, addFlashMessage }: Props) => {
  const { formatMessage } = useIntl();

  const dispatch = useAppDispatch();

  if (!device) {
    return null;
  }

  return (
    <>
      <ModalHeader>
        <ModalHeaderTitle>
          <FormattedMessage id="views.FleetSettings.Telematics.Modals.ForceVIN.title" />
        </ModalHeaderTitle>
      </ModalHeader>
      <Formik
        initialValues={{ [VINField]: '', device: device.imei }}
        validationSchema={() => Validation(formatMessage)}
        onSubmit={async (values, actions) => {
          await dispatch(forceVin(device.id, values[VINField]));
          actions.resetForm();
          mixpanel.track(ANALYTICS_EVENTS.ASSIGN_VIN_TRIGGERED);
          closeModal();
          addFlashMessage(
            formatMessage({ id: 'views.FleetSettings.Telematics.Modals.ForceVIN.success' }),
            FLASH_MESSAGE_TYPES.success
          );
        }}
        enableReinitialize // Ensures we reinitialize the form when the device change (clear errors)
      >
        {(formikProps) => (
          <Form>
            <ModalContent>
              <FormField
                label={formatMessage({ id: 'views.FleetSettings.Telematics.Modals.ForceVIN.device' })}
                name={'device'}
                disabled
              />

              <FormField
                label={formatMessage({ id: 'views.FleetSettings.Telematics.Modals.ForceVIN.vin' })}
                placeholder={formatMessage({ id: 'views.FleetSettings.Telematics.Modals.ForceVIN.vinPlaceholder' })}
                name={VINField}
                required
              />
            </ModalContent>
            <ModalFooter>
              <SecondaryButton onClick={closeModal}>Cancel</SecondaryButton>
              <PrimaryButton
                type="submit"
                disabled={!formikProps.isValid || !formikProps.dirty || formikProps.isSubmitting}
                loading={formikProps.isSubmitting}
              >
                <FormattedMessage id="views.FleetSettings.Telematics.Modals.ForceVIN.forceButton" />
              </PrimaryButton>
            </ModalFooter>
          </Form>
        )}
      </Formik>
    </>
  );
};

const ForceVinModal = () => {
  const { closeModal, device, addFlashMessage } = useForceVin();

  return (
    <Modal>
      <ForceVinForm device={device} closeModal={closeModal} addFlashMessage={addFlashMessage} />
    </Modal>
  );
};

export default ForceVinModal;
